
















































import { Component, Vue, Watch } from 'vue-property-decorator';
import { Action, State, Getter } from 'vuex-class';
import moment from 'moment';
import { actionSetPusherConnected } from './store/actions';
import { EDocumentStatus } from '@signing-page/documents/types/enums/document-status';
import { namespace as entityNamespace } from '@signing-page/entity/store/entity.store';
import { actionLoadTheme } from '@signing-page/entity/store/entity.actions';
import { namespace as sessionNamespace } from '@signing-page/session/store/session.store';
import {
  actionLoadToken,
  actionSubmitAnswer,
  actionSendSmsCode,
  actionSubmitSmsAnswer,
  actionSubmitConsent,
  actionSubmitQnaAnswer,
  actionDeclineConsent,
} from '@signing-page/session/store/session.actions';
import { namespace as document } from '@signing-page/documents/store/document.store';
import { IDocument } from '@signing-page/documents/types/document';
import { ITheme } from '@signing-page/entity/types/theme';
import { BrandingHelper } from '@signing-page/helpers/branding.helper';
import { IAuthenticationStepInput } from '@signing-page/authentication/types/authentication-step-input';
import Lobby from '@signing-page/pages/Lobby.vue';
import { IAuthenticationStep } from '@signing-page/authentication/types/authentication-step';
import { setMomentLocale } from '@/moment';
import { loadLanguageAsync, userLanguage } from '@signing-page/lang';
import {
  Alert as AlertNamespace,
  EAlertAction,
  EAlertState,
} from '@signing-page/alert/store/alert.constants';
import { AlertConstants } from '@lib-shared/alert/constants/alert.constants';
import Alert from '@lib-shared/alert/Alert.vue';
import { EBackgroundType } from '@signing-page/entity/types/enums/background-type';
import { IEntity } from '@signing-page/entity/types/entity';
import { BrowserHelper } from '@signing-page/helpers/browser.helper';
import bugsnagClient from '@lib-shared/bugsnag';
import type { IAlertState } from '@signing-page/alert/store/alert.types';

@Component({
  components: {
    Lobby,
    Alert,
  },
})
export default class App extends Vue {
  @State('document', { namespace: document }) document: IDocument;
  @State('authId') authId;
  @State('documentId') documentId;
  @State('recipientId') recipientId;
  @State('userId') userId;
  @State('isPreview') isPreview: boolean;
  @State('previewExpired') previewExpired: boolean;
  @State('token', { namespace: sessionNamespace }) token;
  @State('isCorrectPin', { namespace: sessionNamespace }) isCorrectPin: boolean;
  @State('status', { namespace: sessionNamespace }) status: EDocumentStatus;
  @State('error', { namespace: sessionNamespace }) sessionError: boolean;
  @State('theme', { namespace: entityNamespace }) theme: ITheme;
  @State('entity', { namespace: entityNamespace }) entity: IEntity;
  @State('authentication', { namespace: sessionNamespace }) authentication;
  @State('error', { namespace: document }) documentError: boolean;

  @Getter('nextAuthStep', { namespace: sessionNamespace }) nextAuthStep: IAuthenticationStep;
  @Getter('documentRecalled', { namespace: sessionNamespace }) documentRecalled: boolean;
  @Getter('consentSteps', { namespace: sessionNamespace }) consentSteps: IAuthenticationStep;
  @Getter('gdprDeclined', { namespace: sessionNamespace }) gdprDeclined: boolean;

  @Action(actionLoadToken, { namespace: sessionNamespace }) actionLoadToken: any;
  @Action(actionSubmitAnswer, { namespace: sessionNamespace }) actionSubmitAnswer: any;
  @Action(actionSendSmsCode, { namespace: sessionNamespace }) actionSendSmsCode: any;
  @Action(actionSubmitSmsAnswer, { namespace: sessionNamespace }) actionSubmitSmsAnswer: any;
  @Action(actionLoadTheme, { namespace: entityNamespace }) actionLoadTheme: any;
  @Action(actionSubmitQnaAnswer, { namespace: sessionNamespace }) actionSubmitQnaAnswer: any;
  @Action(actionSubmitConsent, { namespace: sessionNamespace }) actionSubmitConsent: any;
  @Action('loadEntity', { namespace: entityNamespace }) actionLoadEntity: any;
  @Action(actionDeclineConsent, { namespace: sessionNamespace }) actionDeclineConsent: any;
  @Action(actionSetPusherConnected) actionSetPusherConnected: any;

  @AlertNamespace.State(EAlertState.Queue) alertQueue: IAlertState;
  @AlertNamespace.Action(EAlertAction.Remove) actionRemoveAlert: any;

  showAlert = true;
  documentAvailable = false;

  constructor() {
    super();
  }

  @Watch('hasValidToken')
  watchToken() {
    if (this.hasValidToken) {
      this.loadEntity();
      this.loadPusher();
    }
  }

  get showLobby(): boolean {
    return (
      !!this.nextAuthStep ||
      !!this.consentSteps ||
      this.hasError ||
      this.documentRecalled ||
      this.documentExpired ||
      this.documentRejected ||
      this.previewExpired ||
      this.gdprDeclined
    );
  }
  get pageTitle(): string {
    return this.document ? this.document.name : 'GetAccept';
  }

  get documentExpired(): boolean {
    if (!this.document) {
      return false;
    }
    if (this.document.status === EDocumentStatus.Signed) {
      return false;
    }
    const diff: number = moment.utc(this.document.expirationDate).diff(moment.utc());

    return diff < 0;
  }

  get hasFixedBackground(): boolean {
    return (
      !this.theme ||
      this.theme.backgroundType === EBackgroundType.Fixed ||
      !this.theme.backgroundImage
    );
  }

  get documentRejected(): boolean {
    return !!this.document && this.document.status === EDocumentStatus.Rejected;
  }

  get hasParams(): boolean {
    if (this.isPreview && this.documentId) {
      return true;
    }
    return !!this.authId && !!this.documentId && (!!this.recipientId || !!this.userId);
  }

  get hasError(): boolean {
    return this.sessionError || !this.hasParams || this.documentError;
  }

  get hasValidToken(): boolean {
    const notExpired: boolean =
      (this.token.exp && !moment().isAfter(moment.unix(this.token.exp))) || !this.token.exp;
    const tokenFound = !!this.token.jwt;
    return tokenFound && notExpired;
  }

  get safeBackground(): string {
    return BrandingHelper.getBackground(this.theme, BrowserHelper.isIE(navigator));
  }

  handleSendPin() {
    this.actionSendSmsCode();
  }

  handleSmsAnswer(authStepsInput: IAuthenticationStepInput[]) {
    this.actionSubmitSmsAnswer({ authStepsInput });
  }

  handleQnaAnswer(authStepInput: IAuthenticationStepInput) {
    this.actionSubmitQnaAnswer({
      authStep: authStepInput,
    });
  }

  handleConsentAnswer(authStepsInput: IAuthenticationStepInput[]) {
    this.actionSubmitConsent(authStepsInput);
  }

  handleIdentifyRecipient(authStepInput: IAuthenticationStepInput) {
    this.actionSubmitAnswer([authStepInput]);
  }

  nextAlert() {
    this.showAlert = false;
    this.actionRemoveAlert();
    setTimeout(() => {
      this.showAlert = true;
    }, AlertConstants.snackbarTransition);
  }

  setDocumentTitle() {
    window.document.title = this.pageTitle;
  }

  async loadToken() {
    await this.actionLoadToken({
      authStepsInput: [],
    });
  }

  async loadEntity() {
    await this.actionLoadEntity();
    if (!this.documentRecalled) {
      this.documentAvailable = true;
    }
  }

  async loadPusher() {
    try {
      await this.$pusher.connect(this.token.jwt);
      this.actionSetPusherConnected(true);
    } catch (e) {
      console.error(e);
      if (bugsnagClient) {
        bugsnagClient.notify(e);
      }
    }
  }

  async mounted() {
    if (this.hasParams) {
      await this.actionLoadTheme(this.documentId);
    }

    if (!this.hasValidToken && this.hasParams) {
      await this.loadToken();
    }
    if (this.hasValidToken && this.hasParams && !this.showLobby) {
      await this.loadEntity();
      await this.loadPusher();
    }

    this.setDocumentTitle();
    await loadLanguageAsync(userLanguage());
    await setMomentLocale(userLanguage());
  }
}
